import { Selection, Data, Helpers } from "victory-core";
import { assign, throttle, isFunction, groupBy, keys, isEqual, includes } from "lodash";
import React from "react";

const BisectHelpers = {
	withinBounds(props, point) {
		const { width, height, voronoiPadding, polar, origin, scale } = props;
		const padding = voronoiPadding || 0;
		const { x, y } = point;
		if (polar) {
			const distanceSquared = Math.pow(x - origin.x, 2) + Math.pow(y - origin.y, 2);
			const radius = Math.max(...scale.y.range());
			return distanceSquared < Math.pow(radius, 2);
		} else {
			return x >= padding && x <= width - padding && y >= padding && y <= height - padding;
		}
	},

	withinDomain(props, datapoint) {
		const x = datapoint.x;
		const y = datapoint.y;
		const x_r = props.domain.x;
		const y_r = props.domain.y;
		return x >= x_r[0] && x <= x_r[1] && y >= y_r[0] && y <= y_r[1];
	},

	withinReach(props, datapoint, mousepoint) {
		const y_range = (props.domain.y[1] - props.domain.y[0]) * 0.05;
		return Math.abs(datapoint.y - mousepoint.y) < y_range;
	},

	findNearest(data, timepoint, property) {
		let low = 0;
		let high = data.length - 1;
		let mid = Math.trunc((low + high) / 2);

		/*
		 * notnagel: wenn wir nicht suchen koennen, dann finden wir auch nichts!
		 */
		if (!timepoint || isNaN(timepoint)) {
			return;
		}

		while ((low !== high) && (mid !== low) && (mid !== high)){
			let curr = data[mid][property];
			if (timepoint === curr) {
				return mid;
			}
			else if (timepoint > curr) {
				low = mid;
				mid = Math.trunc((low + high) / 2);
			}
			else if (timepoint < curr) {
				high = mid;
				mid = Math.trunc((low + high) / 2);
			}
		}
		return low;
	},

	getDatasets(props) {
		const addMeta = (data, name, child) => {
			const continuous = child && child.type && child.type.continuous;
			const style = child ? child.props && child.props.style : props.style;
			return data.map((datum, index) => {
				return assign({
					childName: name, eventKey: index, continuous, style
				}, datum);
			});
		};

		if (props.data) {
			return addMeta(props.data);
		}

		const getData = (childProps) => {
			const data = Data.getData(childProps);
			return Array.isArray(data) && data.length > 0 ? data : undefined;
		};

		const iteratee = (child, childName, parent) => {
			const role = child.type && child.type.role;
			const childProps = child.props || {};
			const blacklist = props.voronoiBlacklist || [];
			if (role === "axis" || role === "legend" || role === "label") {
				return null;
			} else if (includes(blacklist, childName)) {
				// ignore any children with names that match the blacklist
				return null;
			} else if (child.type && isFunction(child.type.getData)) {
				child = parent ? React.cloneElement(child, parent.props) : child;
				const childData = childProps
					&& child.type.getData({ ...childProps, domain: props.domain });
				return childData ? addMeta(childData, childName, child) : null;
			} else {
				const childData = getData({ ...childProps, domain: props.domain });
				return childData ? addMeta(childData, childName, child) : null;
			}
		};
		return Helpers.reduceChildren(React.Children.toArray(props.children), iteratee);
	},

	// returns an array of objects with point and data where point is an x, y coordinate, and data is
	// an array of points belonging to that coordinate
	mergeDatasets(props, datasets) {
		const points = groupBy(datasets, (datum) => {
			const { x, y } = Helpers.scalePoint(props, datum);
			return `${x},${y}`;
		});
		return keys(points).map((key) => {
			const point = key.split(",");
			return {
				x: +point[0],
				y: +point[1],
				points: points[key]
			};
		});
	},

	getParentMutation(activePoints, mousePosition) {
		return [{
			target: "parent",
			eventKey: "parent",
			mutation: () => ({ activePoints, mousePosition })
		}];
	},

	onMouseLeave(evt, targetProps) {
		return this.getParentMutation([]);
	},

	onMouseMove(evt, targetProps) { // eslint-disable-line max-statements
		const activePoints = targetProps.activePoints || [];
		const mousePosition = Selection.getSVGEventCoordinates(evt);
		const dataPosition_raw =  Selection.getDataCoordinates(targetProps,
			targetProps.scale, mousePosition.x, mousePosition.y);

		const dataPosition = {
			x: dataPosition_raw.x.valueOf(),
			y: dataPosition_raw.y
		};
		const data = this.getDatasets(targetProps);
		if (data.length < 1 || !this.withinBounds(targetProps, mousePosition)) {
			return this.getParentMutation([], mousePosition);
		}
		/*
		 * hier ist der teil wo wir tatsaechlich einen unter umstaenden
		 * neuen punkt suchen muessen!
		 */
		const np = this.findNearest(data, dataPosition.x, "x");
		/*
		 * wenn die Suche nicht gefunzt hat, dann... ist doff
		 */
		if (!np) {
			return this.getParentMutation([], mousePosition);
		}
		const points = [
			data[np]
		];
		if (!this.withinReach(targetProps, points[0], dataPosition)) {
			return this.getParentMutation([], mousePosition);
		}
		const parentMutations = this.getParentMutation(points, mousePosition);
		if (activePoints.length && isEqual(points, activePoints)) {
			return parentMutations;
		} else {
			return parentMutations.concat([]);
		}
	}
};

export default {
	onMouseLeave: BisectHelpers.onMouseLeave.bind(BisectHelpers),
	onMouseMove: throttle(
		BisectHelpers.onMouseMove.bind(BisectHelpers),
		32, // eslint-disable-line no-magic-numbers
		{ leading: true, trailing: false })
};
